import axios from 'axios';
import { getAuthToken, getAuthUserId, clearAuthSession } from 'utils/authStorage';

// Create an axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate axios instance for blog APIs
const blogApi = axios.create({
  baseURL: process.env.REACT_APP_API_2,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include auth token for main api
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a request interceptor to include auth token for blog api
blogApi.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors for main api
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle unauthorized errors (401)
    if (error.response && error.response.status === 401) {
      clearAuthSession();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors for blog api
blogApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle unauthorized errors (401)
    if (error.response && error.response.status === 401) {
      clearAuthSession();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API service functions
const apiService = {
  // Auth endpoints
  login: (credentials) => api.post('/api/v1/signin', credentials),
  register: (userData) => api.post('/api/v1/signup', userData),
  forgotPassword: (email) => api.post('/api/v1/forgot-password', { email }),
  resetPassword: (data) => api.post('/api/v1/reset-password', data),
  
  // User endpoints
  getUser: (userId) => api.get(`/api/v1/getUser/${userId}`),
  getCurrentUser: () => {
    const userId = getAuthUserId();
    return userId ? api.get(`/api/v1/getUser/${userId}`) : Promise.reject(new Error("Not signed in"));
  },
  getAllUsers: (page = 1, limit = 100) =>
    api.get(`/api/v1/get/all/User?page=${page}&limit=${limit}`),
  getSubAdmins: (userId, role = "SUB_ADMIN") =>
    api.get(`/api/v1/get/all/sub/admin/by/admin/${userId}?role=${role}`),
  getAdminEmployees: () => api.get("/api/v1/admin/employees"),
  createSubAdmin: (userId, data) =>
    api.post(`/api/v1/create/sub/admin/${userId}`, data),
  disableUser: (userId) => api.put(`/api/v1/disable/user/${userId}`),
  updateUser: (userId, data) => api.put(`/api/v1/update/user/${userId}`, data),

  // In-app chat notifications (same API as vendor mobile app)
  getUserNotifications: (userId) =>
    api.get(`/api/v1/get/all/notification/user/${userId}`),
  markNotificationsSeen: (userId) =>
    api.put(`/api/v1/update/users/seen/notification/${userId}`),
  deleteUserNotifications: (userId, data) =>
    api.put(`/api/v1/delete/users/notification/${userId}`, data),
  
  // Business endpoints
  getBusinesses: (page = 1, limit = 10) => api.get(`/api/v1/get/all/Restaurant?page=${page}&limit=${limit}`),
  getBusiness: (id) => api.get(`/api/v1/getRestaurant/${id}`),
  createBusiness: (data) => api.post('/api/v1/create/Restaurant', data),
  updateBusiness: (id, data) => api.put(`/api/v1/update/business/${id}`, data),
  deleteBusiness: (id) => api.delete(`/api/v1/delete/Restaurant/${id}`),
  
  // Member endpoints
  getMembers: (businessId, page = 1, search = '') => api.get(`/api/v1/get/all/Member/${businessId}?page=${page}&search=${search}`),
  getMember: (id) => api.get(`/api/v1/get/customer/detail/${id}`),
  createMember: (data) => api.post('/api/v1/create/Member', data),
  updateMember: (id, data) => api.put(`/api/v1/update/Member/${id}`, data),
  deleteMember: (id) => api.delete(`/api/v1/delete/Member/${id}`),
  
  // Plan endpoints
  getPlans: (businessId) => api.get(`/api/v1/get/all/Plan/${businessId}`),
  getPlan: (id) => api.get(`/api/v1/get/Plan/${id}`),
  createPlan: (data) => api.post('/api/v1/create/Plan', data),
  updatePlan: (id, data) => api.put(`/api/v1/update/Plan/${id}`, data),
  deletePlan: (id) => api.delete(`/api/v1/delete/Plan/${id}`),
  
  // CRM activity logs
  getLogs: (userId, params = {}) => api.get(`/api/v1/get/log/${userId}`, { params }),

  // Help endpoints
  getHelps: () => api.get('/api/v1/get/all/help'),
  getHelp: (id) => api.get(`/api/v1/get/help/${id}`),
  createHelp: (data) => api.post('/api/v1/create/help', data),
  updateHelp: (id, data) => api.put(`/api/v1/update/help/${id}`, data),
  deleteHelp: (id) => api.delete(`/api/v1/delete/help/${id}`),
  
  // Review endpoints
  getReviews: (businessId) => api.get(`/api/v1/get/all/review/${businessId}`),
  updateReview: (id, data) => api.put(`/api/v1/update/review/${id}`, data),
  
  // Attendance endpoints
  getAttendance: (businessId, date) => api.get(`/api/v1/get/all/attendance/${businessId}?date=${date}`),
  
  // Leave Request endpoints
  getLeaveRequests: (businessId) => api.get(`/api/v1/get/all/leaveRequest/${businessId}`),
  updateLeaveRequest: (id, data) => api.put(`/api/v1/update/leaveRequest/${id}`, data),

  // Customer enquiry endpoints
  getAllCustomerEnquiries: (params = {}) =>
    api.get("/api/v1/admin/enquiries", { params }),

  getDirectoryEnquiries: (params = {}) =>
    api.get("/api/v1/admin/directory-enquiries", { params }),
  updateDirectoryEnquiryStatus: (id, data) =>
    api.patch(`/api/v1/admin/directory-enquiries/${id}/status`, data),

  // Lead management endpoints
  getLeads: (params = {}) => api.get("/api/v1/admin/leads", { params }),
  getBusinessLeads: (businessId, params = {}) =>
    api.get(`/api/v1/admin/business/${businessId}/leads`, { params }),
  getLead: (leadId) => api.get(`/api/v1/admin/leads/${leadId}`),
  getLeadStats: (params = {}) =>
    api.get("/api/v1/admin/leads/stats", { params }),
  createLead: (data) => api.post("/api/v1/admin/leads", data),
  updateLead: (leadId, data) =>
    api.put(`/api/v1/admin/leads/${leadId}`, data),
  deleteLead: (leadId) => api.delete(`/api/v1/admin/leads/${leadId}`),
  assignLead: (leadId, data) =>
    api.put(`/api/v1/admin/leads/${leadId}/assign`, data),
  updateLeadStatus: (leadId, data) =>
    api.put(`/api/v1/admin/leads/${leadId}/status`, data),
  updateLeadFollowUp: (leadId, data) =>
    api.put(`/api/v1/admin/leads/${leadId}/follow-up`, data),
  transferLead: (leadId, data) =>
    api.post(`/api/v1/admin/leads/${leadId}/transfer`, data),
  createLeadFromBusiness: (businessId, data) =>
    api.post(`/api/v1/admin/leads/from-business/${businessId}`, data),

  // Employee targets & performance
  getEmployeeTargets: (params = {}) =>
    api.get("/api/v1/admin/employee-targets", { params }),
  upsertEmployeeTarget: (data) =>
    api.put("/api/v1/admin/employee-targets", data),
  getEmployeePerformance: (params = {}) =>
    api.get("/api/v1/admin/employee/performance", { params }),
  
  // FCM Token
  sendAdminToken: (data) => api.post('/api/v1/sendAdminToken', data),
  
  // QR Code
  getQRCode: (businessId) => api.get(`/api/v1/get/Restaurant/qrCode/${businessId}`),
  
  // Blog endpoints
  getBlogs: () => blogApi.get('/get/all/blog'),
  getBlog: (id) => blogApi.get(`/get/blog/${id}`),
  createBlog: (data) => {
    const userId = getAuthUserId();
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('content', data.content);
    formData.append('description', data.description);
    if (data.thumbnail) {
      formData.append('thumbnail', data.thumbnail);
    }
    return blogApi.post(`/create/blog/${userId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  updateBlog: (id, data) => {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('content', data.content);
    formData.append('description', data.description);
    if (data.thumbnail) {
      formData.append('thumbnail', data.thumbnail);
    }
    return blogApi.put(`/update/blog/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteBlog: (id) => blogApi.delete(`/delete/blog/${id}`),

  // Notification endpoints
  getScheduledNotifications: () => blogApi.get('/notification/getScheduleNotification'),
  sendNotification: (data) => {
    const formData = new FormData();
    formData.append('title', data.title || '');
    formData.append('description', data.description || '');
    formData.append('link', data.link || '');
    formData.append('type', data.type || 'members');
    formData.append('send', data.send || 'BOTH');
    const scheduleFlag = data.scheduleJob ? 'true' : 'false';
    formData.append('scheduleJob', scheduleFlag);
    formData.append('dateTime', data.dateTime || '');

    if (data.icon) {
      formData.append('icon', data.icon);
    }

    return blogApi.post('/send/notification', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /** Super-admin: FCM push to vendor business owners only (main CRM API). */
  sendOwnerBroadcast: ({ title, description, image }) =>
    api.post('/api/v1/admin/broadcast/owners', {
      title: title || '',
      description: description || '',
      ...(image?.trim() ? { image: image.trim() } : {}),
    }),

  // Dashboard
  getDashboard: (params = {}) => api.get('/api/v1/get/dashboard', { params }),

  // HMT categories
  getHmtCategories: () => api.get('/api/v1/hmtplan/getAllCategories'),
  createHmtCategory: (formData) =>
    api.post('/api/v1/hmtplan/createCategory', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateHmtCategory: (id, formData) =>
    api.put(`/api/v1/hmtplan/updateCategory/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteHmtCategory: (id) => api.delete(`/api/v1/hmtplan/deleteCategory/${id}`),

  // HMT plans
  getHmtPlansByCategory: (categoryId) =>
    api.get(`/api/v1/hmtplan/getPlan/${categoryId}`),
  getHmtPlanDetail: (id) => api.get(`/api/v1/hmtplan/getPlanDetail/${id}`),
  createHmtPlan: (data) => api.post('/api/v1/hmtplan/create', data),
  updateHmtPlan: (id, data) => api.put(`/api/v1/hmtplan/update/${id}`, data),

  // Bulk orders
  getBulkOrders: () => api.get('/api/v1/get/all/requestBulkOrder'),

  // Location helpers for plan form
  getStates: () => api.get('/api/v1/get/state'),
  getCities: (stateId) => api.get(`/api/v1/get/city/${stateId}`),

  // ApnaThali HMT purchase sales
  getApnaThaliPurchasePlans: (params = {}) =>
    api.get("/api/v1/get/all/purchase/memberPlans/ApnaThali", { params }),

  // Invoice endpoints
  getSalesInvoices: (params = {}) => api.get('/api/v1/invoices/sales', { params }),
  getAllInvoices: () => api.get('/api/v1/invoices'),
  downloadInvoice: (invoiceId) =>
    api.put(`/api/v1/invoice/${invoiceId}/status`, { status: 'DOWNLOADED' }),
};

export default apiService;